import React from "react";
import { IconCorporateFareBlue } from "assets/svg";
import { getPublicAssetUrl } from "components/productSellerCompanyCard/productSellerCompanyCard.utils";
import {
  useGetCompanyProfileQuery,
  useGetMyBadgesQuery,
} from "reducers/companyProfileApi";
import { useGetMyXpQuery } from "reducers/expApi";
import {
  CompanyProfileCard,
  CompanyProfileCardTitle,
} from "./CompanyProfileCard";

interface CompanyLevelCardProps {
  companyCode?: string;
}

// Ngày cập nhật: luôn là hôm nay theo giờ Nhật (YYYY/M/D).
const getJstTodayLabel = (): string =>
  new Date().toLocaleDateString("ja-JP", {
    timeZone: "Asia/Tokyo",
    year: "numeric",
    month: "numeric",
    day: "numeric",
  });

const CompanyLevelCard = ({ companyCode }: CompanyLevelCardProps) => {
  const {
    data: xpData,
    isLoading: isXpLoading,
    isError: isXpError,
  } = useGetMyXpQuery();
  const { data: badges = [] } = useGetMyBadgesQuery();
  const { data: profileData } = useGetCompanyProfileQuery(
    { companyCode: companyCode ?? "" },
    { skip: !companyCode },
  );

  const level = xpData?.data.current_level;
  // Loading/error đều hiển thị "Lv.–" để giữ nguyên layout card.
  const levelLabel = isXpLoading || isXpError || level == null ? "–" : level;

  const levelsGainedThisMonth = xpData?.data.levels_gained_this_month ?? 0;

  // Chỉ hiển thị badge công ty đã claim (đã mở khoá).
  const claimedBadges = badges.filter((badge) => badge.is_unlocked);

  const logoUrl = profileData?.data.logo_url;

  return (
    <CompanyProfileCard className="flex flex-col xl:min-h-[297px]">
      <CompanyProfileCardTitle
        action={
          <span className="font-['Noto_Sans_JP'] text-[12px] leading-[1.6] text-white">
            最終更新日：{getJstTodayLabel()}
          </span>
        }
      >
        構造レベル
      </CompanyProfileCardTitle>

      <div className="flex flex-1 flex-col justify-center gap-[20px] py-[20px]">
        {/* Hàng cấp độ: avatar công ty (nếu có) + Lv */}
        <div className="flex items-center gap-[15px]">
          <div className="flex h-[60px] w-[60px] shrink-0 items-center justify-center overflow-hidden rounded-full bg-white">
            {logoUrl ? (
              <img
                src={getPublicAssetUrl(logoUrl)}
                alt="company avatar"
                className="h-full w-full object-cover"
              />
            ) : (
              <IconCorporateFareBlue
                aria-hidden="true"
                className="h-[30px] w-[30px] text-[#1b4b86]"
              />
            )}
          </div>
          <div className="font-['Audiowide'] text-[42px] leading-none text-[#d4f754] sm:text-[50px]">
            Lv.{levelLabel}
          </div>
          {levelsGainedThisMonth > 0 && (
            <span className="rounded-full bg-[#d4f754] px-[12px] py-[4px] font-['Audiowide'] text-[14px] leading-none text-[#1b4b86]">
              +{levelsGainedThisMonth} UP
            </span>
          )}
        </div>

        {/* Hàng huy hiệu công ty đã claim */}
        <div className="rounded-[6px] bg-[#002959]/60 p-[12px]">
          <div className="flex gap-[10px] overflow-x-auto">
            {claimedBadges.length > 0 ? (
              claimedBadges.map((badge) => (
                <img
                  key={badge.code}
                  src={getPublicAssetUrl(badge.icon_url)}
                  alt={badge.name}
                  title={badge.name}
                  className="h-[64px] w-[64px] shrink-0 object-contain"
                />
              ))
            ) : (
              <span className="py-[20px] font-['Noto_Sans_JP'] text-[13px] leading-[1.6] text-white/70">
                獲得したバッジはありません
              </span>
            )}
          </div>
        </div>
      </div>
    </CompanyProfileCard>
  );
};

export default CompanyLevelCard;
